import React, { useState, useEffect, useMemo } from "react";
import { Auth } from "aws-amplify";
import { useSelector } from "react-redux";
import { TrackingProvider } from '@rehrig/tracking-sdk';
import TrackedErrorBoundary from "./components/tracking/TrackedErrorBoundary";
import SessionTracker from "./components/tracking/SessionTracker";
import AuthRoutes from "./AuthRoutes";

// Returns a fresh JWT token on every call - Amplify caches the session internally
// and only makes a network call when the token is near expiry (once per hour max).
// This is the same pattern used by all other APIs in this project (AuthThunk.jsx).
const getToken = async () => {
  try {
    const session = await Auth.currentSession();
    return session.getAccessToken().getJwtToken();
  } catch {
    return '';
  }
};

function App() {
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);

  // Get authStatus and central user data from Redux
  const authStatus = useSelector((state) => state.auth?.authStatus || 'unauthenticated');
  const userData = useSelector((state) => state.auth?.userData?.Data);
  const selectedCustomer = useSelector((state) => state.Routes?.selectedCustomerListData);
  const selectedLocation = useSelector((state) => state.Routes?.selectedLocationListData);

  // Just wait for initial auth check - no need to store the token in state
  useEffect(() => {
    Auth.currentSession()
      .catch(() => {})
      .finally(() => setIsLoadingAuth(false));
  }, []);

  const trackingApiUrl = process.env.REACT_APP_TRACKING_API_URL || "";
  // Only enable tracking when userData is available to avoid sending events with null user context
  // TrackingProvider will also check backend config.enabled from API
  const hasUserData = !!userData;
  const trackingEnabled = !!trackingApiUrl && hasUserData;

  // Compute user enrichment context reactively - updates when userData changes
  const userEnrichment = useMemo(() => {
    // Extract user context from Central API userData
    // Priority: email first, fallback to username
    const userIdentifier = userData?.email || userData?.username || null;
    
    // Use central_customer_id as the customer identifier
    const customerIdentifier = userData?.central_customer_id 
      ? String(userData.central_customer_id) 
      : null;
    
    // For location, use all location IDs from Central API as comma-separated string
    let locationIdentifier = null;
    if (userData?.locations && userData.locations.length > 0) {
      locationIdentifier = userData.locations.join(',');
    } else if (selectedLocation?.locationId) {
      locationIdentifier = String(selectedLocation.locationId);
    }

    return {
      user_id_hash: userIdentifier,
      customer_name: customerIdentifier,
      location_name: locationIdentifier,
      role_name: userData?.central_role_id ? String(userData.central_role_id) : null,
    };
  }, [userData, trackingApiUrl]);

  // Wait for auth token before rendering
  if (isLoadingAuth) {
    return null;
  }

  return (
    <React.Fragment>
      <TrackingProvider
        appName="iris-portal"
        apiBaseUrl={trackingApiUrl}
        authToken={getToken}
        enabled={trackingEnabled}
        disabledEventTypes={['BUTTON_CLICK']}
        userEnrichment={userEnrichment}
      >
        <SessionTracker authStatus={authStatus} />
        <TrackedErrorBoundary>
          <AuthRoutes />
        </TrackedErrorBoundary>
      </TrackingProvider>
    </React.Fragment>
  );
}

export default App;
